import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SendResourceButton } from "@/components/resources/SendResourceDialog";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Stethoscope, Plus, X, Save, CheckCircle2, ClipboardList, Mic, AlertCircle,
  AlertTriangle,
  ChevronDown, ChevronUp, RotateCw, Download, ShieldAlert, Sparkles, History, Search, Wand2,
  Link2, Loader2, Trash2, Unlock,
} from "lucide-react";
import { LinkedIntakeControl } from "@/components/assessment/LinkedIntakeControl";
import { PrintFormButton } from "@/components/assessment/print/PrintFormDialog";
import { MeasurementTrend } from "@/components/assessment/MeasurementTrend";
import { metricsFromResponses } from "@/lib/bodyMetrics";
import { toast } from "sonner";
import SoapSessionDialog from "@/components/SoapSessionDialog";
import SuggestionComparisonDialog from "@/components/SuggestionComparisonDialog";
import ReportTranscriptionErrorDialog from "@/components/ReportTranscriptionErrorDialog";
import EvidenceDrawer from "@/components/EvidenceDrawer";
import TranscriptSuggestionPill from "@/components/TranscriptSuggestionPill";
import TranscriptReviewPane from "@/components/TranscriptReviewPane";
import useTranscriptSuggestions from "@/hooks/useTranscriptSuggestions";
import MskRegionsPanel from "@/components/MskRegionsPanel";
import DeleteWithReasonDialog from "@/components/DeleteWithReasonDialog";
import { useAuth } from "@/lib/auth";
import { useRecording } from "@/lib/recording";
import { useActiveClient } from "@/lib/activeClient";
import { SbnResultsSection } from "@/components/assessment/sbn/SbnResultsSection";
import { GripNormsHint } from "@/components/assessment/sbn/GripNormsHint";

const CONSENT_OPTIONS = [
  { v: "verbal", label: "Verbal consent obtained" },
  { v: "written", label: "Written consent obtained" },
  { v: "declined", label: "Declined recording" },
  { v: "withdrawn", label: "Consent withdrawn" },
  { v: "not_required", label: "Not required (approved workflow)" },
];

function ResponseChip({ resp }) {
  if (!resp) return null;
  const map = {
    unreviewed: { label: "From intake", cls: "border-sky-200 bg-sky-50 text-sky-800" },
    verified:   { label: "Verified", cls: "border-emerald-200 bg-emerald-50 text-emerald-800" },
    amended:    { label: "Amended by clinician", cls: "border-sky-200 bg-sky-50 text-sky-800" },
    declined:   { label: "Declined", cls: "border-zinc-200 bg-zinc-50 text-zinc-700" },
    not_applicable: { label: "Not applicable", cls: "border-zinc-200 bg-zinc-50 text-zinc-700" },
    superseded: { label: "Superseded", cls: "border-zinc-200 bg-zinc-50 text-zinc-700" },
  };
  const m = map[resp.verification] || { label: resp.verification, cls: "border-zinc-200 bg-zinc-50 text-zinc-700" };
  return (
    <span className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${m.cls}`}>
      {m.label}
    </span>
  );
}

import {
  evaluateShowIf as v2EvaluateShowIf,
  applyAnswerPatch as v2ApplyAnswerPatch,
  getSafetyAlerts as v2GetSafetyAlerts,
  ALERT_RULE_MESSAGES as V2_ALERT_MESSAGES,
} from "@/lib/formEngine";

function evaluateShowIf(field, responses) {
  // v2.0 shape — {fieldId, operator, value} — delegated to the shared
  // engine so behaviour matches the public form filler exactly.
  if (field?.showIf) {
    const flat = flattenResponses(responses);
    return v2EvaluateShowIf(field, flat);
  }
  // Legacy shape kept for pre-v2 modules: {field, equals/not_equals/…}.
  const legacy = field?.show_if;
  if (!legacy || typeof legacy !== "object") return true;
  const val = responses?.[legacy.field]?.value;
  if ("equals" in legacy) return val === legacy.equals;
  if ("not_equals" in legacy) return val !== legacy.not_equals;
  if ("greater_than" in legacy) return Number(val) > Number(legacy.greater_than);
  if ("less_than" in legacy) return Number(val) < Number(legacy.less_than);
  if ("answered" in legacy) return val !== undefined && val !== "" && val !== null;
  return true;
}

function flattenResponses(responses) {
  const out = {};
  for (const [k, v] of Object.entries(responses || {})) {
    out[k] = v?.value;
  }
  return out;
}


import { FieldRenderer } from "@/components/assessment/FieldRenderer";
import {
  OnboardingChangesBanner, OnboardingChangesDialog, OnboardingChangeChip,
} from "@/components/assessment/OnboardingChanges";
export { FieldRenderer };

export default function AssessmentEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [assessment, setAssessmentRaw] = useState(null);
  const [showOnboardingChanges, setShowOnboardingChanges] = useState(false);
  // Keep the onboarding-change list when an endpoint returns the doc without it.
  const setAssessment = useCallback((next) => setAssessmentRaw((prev) => {
    const val = typeof next === "function" ? next(prev) : next;
    if (val && prev) {
      const keep = {};
      if (val.onboarding_changes === undefined) keep.onboarding_changes = prev.onboarding_changes;
      if (val.recording_consent_on_file === undefined) keep.recording_consent_on_file = prev.recording_consent_on_file;
      if (val.linked_intake === undefined && val.linked_intake_id === prev.linked_intake_id) keep.linked_intake = prev.linked_intake;
      return { ...val, ...keep };
    }
    return val;
  }), []);
  const [schemas, setSchemas] = useState({});   // {module_key: full schema}
  const [modules, setModules] = useState([]);   // registry
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("saved");  // saved | saving | dirty | error
  const [openSections, setOpenSections] = useState({});
  const [pendingChanges, setPendingChanges] = useState({});
  // Mirror of pendingChanges/assessment.id kept in a ref so
  // synchronous unmount / beforeunload cleanup can access the latest
  // values without waiting on a state closure.
  const pendingChangesRef = useRef({});
  const assessmentIdRef = useRef(null);
  const readOnlyRef = useRef(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [showAddModule, setShowAddModule] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [linkRecordingOpen, setLinkRecordingOpen] = useState(false);
  const [previousResponses, setPreviousResponses] = useState({});
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState("");
  const [signatures, setSignatures] = useState([]);
  const [signatureId, setSignatureId] = useState("");   // "" = use default
  const [showFinaliseDialog, setShowFinaliseDialog] = useState(false);
  const [finalising, setFinalising] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showReopen, setShowReopen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [reopening, setReopening] = useState(false);
  const { can, user } = useAuth();
  const isAdmin = can("owner", "manager");
  const saveTimer = useRef(null);

  const readOnly = assessment?.status === "finalised";
  const changesByField = useMemo(
    () => new Map((assessment?.onboarding_changes || []).map((c) => [c.field_key, c])),
    [assessment?.onboarding_changes],
  );
  // Reopen visible only to the Owner OR the practitioner who originally
  // finalised the record — matches the backend permission gate and the
  // SOAP audit-trail-editing pattern.
  const canReopen = !!(
    readOnly && user && (
      user.role === "owner"
      || String(user.id || user.email || "") === String(assessment?.finalised_by || "")
    )
  );
  const amendmentCount = (assessment?.amendment_history || []).length;

  // Phase 4 — pathway-side duplicate fields (marked bindsToCanonical
  // in the module schema) render read-only with an "Edit in Core →"
  // affordance that expands the Core module, scrolls to the owning
  // field and briefly highlights it.
  const jumpToCoreField = useCallback((coreFieldKey) => {
    if (!coreFieldKey) return;
    const coreSchema = schemas["core"];
    if (coreSchema) {
      setOpenSections((prev) => {
        const next = { ...prev };
        (coreSchema.sections || []).forEach((s) => {
          next[`core::${s.key}`] = true;
        });
        return next;
      });
    }
    setTimeout(() => {
      const el = document.querySelector(`[data-field-wrapper="${coreFieldKey}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-indigo-400", "ring-offset-2", "rounded-md", "transition");
      setTimeout(() => {
        el.classList.remove("ring-2", "ring-indigo-400", "ring-offset-2", "rounded-md", "transition");
      }, 1800);
    }, 120);
  }, [schemas]);

  // Set of field keys the last canonical-prefill call auto-populated.
  // Used to render a small "auto-filled from onboarding" chip beside
  // the label so the clinician sees the provenance without having
  // to click into history.
  const [autoFilledFields, setAutoFilledFields] = useState(new Set());
  const [manualPrefilling, setManualPrefilling] = useState(false);

  // Transcript-suggestion state + actions live in a dedicated hook so
  // this component focuses on the assessment form itself.
  const refreshAssessment = useCallback(async () => {
    if (!id) return;
    const r = await api.get(`/assessments/${id}`);
    setAssessment(r.data);
  }, [id, setAssessment]);
  // Keep the header "working on" client chip on whoever this assessment is for.
  const { setClient: setActiveClient } = useActiveClient();
  useEffect(() => {
    const email = extractEmailFromClientId(assessment?.client_id);
    if (email) setActiveClient({ email, name: assessment.client_name || "", canonical_client_id: assessment.canonical_client_id });
  }, [assessment?.client_id, assessment?.client_name, assessment?.canonical_client_id, setActiveClient]);

  // Recording started from this assessment: refresh once it's linked
  // (on start) and again once the transcript suggestions are ready.
  const rec = useRecording();
  const recForThis = !!id && rec.session?.initiating_assessment_id === id;
  const recSessionId = recForThis ? rec.session.id : null;
  const recActiveForThis = recForThis && rec.isActive;
  const refreshSuggestionsRef = useRef(null);
  useEffect(() => {
    if (!recSessionId) return;
    if (rec.status === "recording") { refreshAssessment().catch(() => {}); return; }
    if (rec.status !== "done") return;
    let live = true;
    (async () => {
      const t0 = Date.now();
      while (live && Date.now() - t0 < 180000) {
        try {
          const r = await api.get(`/soap-notes/sessions/${recSessionId}`);
          if (r.data?.session?.suggestions_status !== "extracting") break;
        } catch { /* keep waiting */ }
        await new Promise((res) => setTimeout(res, 4000));
      }
      if (!live) return;
      await Promise.all([refreshAssessment(), refreshSuggestionsRef.current?.()]).catch(() => {});
      toast.success("Recording linked to this assessment", {
        description: "Transcript suggestions are ready to review.",
        position: "top-center",
      });
    })();
    return () => { live = false; };
  }, [recSessionId, rec.status, refreshAssessment]);
  const tx = useTranscriptSuggestions({
    assessmentId: id,
    readOnly,
    refreshAssessment,
  });
  const {
    suggestions, suggestionsLoading,
    suggestionsByField, pendingBySectionKey, filteredSuggestions,
    speakerFilter, experiencerFilter,
    setSpeakerFilter, setExperiencerFilter,
    comparisonSuggestionId, setComparisonSuggestionId,
    reportSuggestion, setReportSuggestion,
    evidenceSuggestion, setEvidenceSuggestion,
    generateSuggestions, refreshSuggestions,
    acceptSuggestion, rejectSuggestion, deferSuggestion,
    clarifySuggestion, batchAcceptSafe,
  } = tx;
  refreshSuggestionsRef.current = refreshSuggestions;

  async function loadAssessment() {
    setLoading(true);
    try {
      const r = await api.get(`/assessments/${id}`);
      setAssessmentRaw(r.data);
      // The GET auto-prefills on first open (and for newly-added modules).
      // Later onboarding changes are flagged, never filled silently.
      const seeded = r.data?.auto_prefill_seeded || [];
      const filled = new Set(seeded.map((s) => s.field_key));
      for (const [k, resp] of Object.entries(r.data?.responses || {})) {
        if (resp?.prefilled_from) filled.add(k);
      }
      setAutoFilledFields(filled);
      if (seeded.length > 0) {
        toast.success(`Pre-filled ${seeded.length} field${seeded.length === 1 ? "" : "s"} from onboarding`);
      }
    } catch (e) {
      toast.error("Could not load assessment", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }

  async function loadModuleSchema(key) {
    if (schemas[key]) return schemas[key];
    try {
      const r = await api.get(`/assessments/modules/${key}`);
      setSchemas((prev) => ({ ...prev, [key]: r.data }));
      return r.data;
    } catch { return null; }
  }

  async function loadModuleRegistry() {
    try {
      const r = await api.get("/assessments/modules");
      setModules(r.data?.modules || []);
    } catch { /* ignore */ }
  }

  async function prefillFromOnboarding() {
    if (readOnly) return;
    setManualPrefilling(true);
    try {
      const r = await api.post(`/assessments/${id}/canonical-prefill`);
      const seeded = r.data?.seeded || [];
      if (r.data?.assessment) setAssessment(r.data.assessment);
      if (seeded.length === 0) {
        toast.info("No new fields to prefill", {
          description: (
            "Every onboarding answer that maps to this assessment is already filled in " +
            "(or was declined / deferred on the Onboarding Review tab)."
          ),
          duration: 6000,
        });
        return;
      }
      setAutoFilledFields((prev) => {
        const next = new Set(prev);
        for (const s of seeded) next.add(s.field_key);
        return next;
      });
      const preview = seeded
        .slice(0, 5)
        .map((s) => s.field_key)
        .join(", ");
      const rest = seeded.length > 5 ? `, and ${seeded.length - 5} more` : "";
      toast.success(`Prefilled ${seeded.length} field${seeded.length === 1 ? "" : "s"} from onboarding`, {
        description: `${preview}${rest}`,
        duration: 6000,
      });
    } catch (e) {
      toast.error("Prefill failed", { description: e?.response?.data?.detail || e.message });
    } finally { setManualPrefilling(false); }
  }

  useEffect(() => { loadAssessment(); loadModuleRegistry(); }, [id]);

  // Load practitioner signature blocks — mirrors the Letters flow.
  useEffect(() => {
    api.get("/settings/signatures")
      .then((r) => setSignatures(r.data?.signatures || []))
      .catch(() => setSignatures([]));
  }, []);

  // Sync locally-selected signature with what the server has stored.
  useEffect(() => {
    if (!assessment) return;
    setSignatureId(assessment.signature_id || "");
  }, [assessment?.signature_id]);

  async function saveSignatureSelection(newId) {
    // Optimistic — flip the picker immediately, then persist.
    setSignatureId(newId);
    if (readOnly) return;
    try {
      const r = await api.post(`/assessments/${id}/signature`, {
        signature_id: newId || null,
      });
      setAssessment(r.data);
    } catch (e) {
      toast.error("Could not save signature", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  useEffect(() => {
    if (!assessment?.previous_assessment_id) return;
    api.get(`/assessments/${assessment.id}/previous-responses`)
      .then((r) => setPreviousResponses(r.data || {}))
      .catch(() => setPreviousResponses({}));
  }, [assessment?.id, assessment?.previous_assessment_id]);

  useEffect(() => {
    if (!assessment) return;
    (assessment.modules || []).forEach((m) => loadModuleSchema(m.key));
    // Expand first visible section by default
    const first = (assessment.modules || []).find((m) => !m.hidden);
    if (first && schemas[first.key]) {
      const s1 = schemas[first.key].sections?.[0];
      if (s1) setOpenSections((p) => ({ [`${first.key}::${s1.key}`]: true, ...p }));
    }
    // eslint runs of this effect intentionally depend only on assessment id
  }, [assessment?.id]);

  // Debounced autosave
  // Debounce window in ms — 1200 feels responsive (matches the EP
  // editor's autosave cadence) without hammering the API mid-word.
  const AUTOSAVE_DEBOUNCE_MS = 1200;

  const flushChanges = useCallback(async () => {
    if (!assessment || Object.keys(pendingChanges).length === 0) return;
    setSaveState("saving");
    // Snapshot of exactly what we're sending — used AFTER the PATCH
    // completes to merge the same values into assessment.responses,
    // but only for fields the user hasn't re-edited during the
    // request in-flight (race protection).
    const inFlight = { ...pendingChanges };
    try {
      const patch = {};
      for (const [k, v] of Object.entries(inFlight)) {
        patch[k] = { value: v };
      }
      const r = await api.patch(`/assessments/${assessment.id}/responses`, { responses: patch });
      // Merge (a) server-authoritative metadata (version, audit,
      // updated_at, alerts) AND (b) the just-saved response values
      // into assessment.responses so localResponses no longer needs
      // the pendingChanges overlay for those fields.
      //
      // We keep the local `responses` object identity where possible
      // so React doesn't remount unrelated field renderers, and we
      // replace ONLY the entries whose (fieldKey → value) matches
      // what we just sent. That way we don't clobber user edits made
      // between the PATCH firing and the response arriving.
      setAssessment((prev) => {
        if (!prev) return prev;
        const nextResponses = { ...(prev.responses || {}) };
        for (const [k, v] of Object.entries(inFlight)) {
          nextResponses[k] = { ...(nextResponses[k] || {}), value: v };
        }
        return {
          ...prev,
          responses: nextResponses,
          version: r.data.version ?? prev.version,
          updated_at: r.data.updated_at ?? prev.updated_at,
          updated_by: r.data.updated_by ?? prev.updated_by,
          alerts: r.data.alerts ?? prev.alerts,
          audit: r.data.audit ?? prev.audit,
        };
      });
      // Clear ONLY the keys we just saved — any change the user
      // made mid-flight stays in pendingChanges and the next
      // debounce cycle picks it up.
      setPendingChanges((prev) => {
        const next = { ...prev };
        for (const [k, v] of Object.entries(inFlight)) {
          if (next[k] === v) delete next[k];
        }
        pendingChangesRef.current = next;
        return next;
      });
      setSaveState("saved");
      setLastSavedAt(Date.now());
    } catch (e) {
      setSaveState("error");
      toast.error("Autosave failed", { description: e?.response?.data?.detail || e.message });
    }
  }, [assessment, pendingChanges, setAssessment]);

  // Keep the refs in sync — used by unmount / beforeunload flush
  // where we need to fire a final save without waiting on state.
  useEffect(() => { pendingChangesRef.current = pendingChanges; }, [pendingChanges]);
  useEffect(() => { assessmentIdRef.current = assessment?.id || null; }, [assessment?.id]);
  useEffect(() => { readOnlyRef.current = readOnly; }, [readOnly]);

  useEffect(() => {
    if (Object.keys(pendingChanges).length === 0) return;
    setSaveState("dirty");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { flushChanges(); }, AUTOSAVE_DEBOUNCE_MS);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [pendingChanges, flushChanges]);

  // Best-effort flush when the tab is being closed / refreshed.
  // The Beacon API is used because standard fetch calls are
  // cancelled mid-flight by the browser during unload.
  useEffect(() => {
    function handleBeforeUnload(e) {
      const pc = pendingChangesRef.current || {};
      if (Object.keys(pc).length === 0) return;
      if (readOnlyRef.current) return;
      const aid = assessmentIdRef.current;
      if (!aid) return;
      const patch = {};
      for (const [k, v] of Object.entries(pc)) patch[k] = { value: v };
      try {
        const url = `${api.defaults.baseURL}/assessments/${aid}/responses`;
        const body = new Blob(
          [JSON.stringify({ responses: patch })],
          { type: "application/json" },
        );
        // sendBeacon is fire-and-forget but reliably survives unload
        // in every modern browser. Auth cookies ride along
        // automatically because withCredentials is baked into the
        // fetch cookie jar for same-origin requests.
        navigator.sendBeacon?.(url, body);
      } catch { /* ignore — best effort */ }
      // Show the native "Leave without saving?" prompt only if the
      // beacon send didn't cover it (older browsers, private mode).
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Unmount flush: if the user navigates away inside the SPA while
  // a debounce is pending, fire the final PATCH so no edit is
  // silently dropped. Uses the refs so we don't need to depend on
  // the state closure.
  useEffect(() => {
    return () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      const pc = pendingChangesRef.current || {};
      const aid = assessmentIdRef.current;
      if (!aid || readOnlyRef.current || Object.keys(pc).length === 0) return;
      const patch = {};
      for (const [k, v] of Object.entries(pc)) patch[k] = { value: v };
      // We intentionally don't await — cleanup can't be async.
      api.patch(`/assessments/${aid}/responses`, { responses: patch })
        .catch(() => { /* silent — leaving the page anyway */ });
    };
    // Intentional: only run on true unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setFieldValue(fieldKey, value) {
    if (readOnly) return;
    // v2.0 exclusive-rule enforcement: setting one field may need
    // to clear peers (`exclusiveGroup`) or listed field ids
    // (`exclusiveWith`). We flatten responses + pending changes,
    // let the engine compute the fully-patched map, then queue any
    // fields whose values differ into `pendingChanges` so the
    // debounced save picks them up.
    setPendingChanges((prev) => {
      const currentFlat = flattenResponses({
        ...(assessment?.responses || {}),
        ...Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, { value: v }])),
      });
      const allFields = collectAllFields(schemasRef.current);
      const patched = v2ApplyAnswerPatch(allFields, currentFlat, fieldKey, value);
      const next = { ...prev };
      for (const [k, v] of Object.entries(patched)) {
        if (currentFlat[k] !== v) next[k] = v;
      }
      // Always record the primary change even if the patch is a no-op elsewhere.
      next[fieldKey] = value;
      return next;
    });
  }

  // Ref-backed cache of schemas — used by setFieldValue's exclusive
  // rule sweep so we don't retrigger renders on every keystroke.
  const schemasRef = useRef({});
  useEffect(() => { schemasRef.current = schemas; }, [schemas]);

  function collectAllFields(schemas) {
    const out = [];
    for (const key of Object.keys(schemas || {})) {
      const schema = schemas[key];
      for (const s of (schema?.sections || [])) {
        for (const f of (s.fields || [])) {
          // Normalise shape for formEngine (fields have `key` not `id`).
          out.push({ ...f, id: f.key });
        }
      }
    }
    return out;
  }

  const localResponses = useMemo(() => {
    const base = { ...(assessment?.responses || {}) };
    for (const [k, v] of Object.entries(pendingChanges)) {
      base[k] = { ...(base[k] || {}), value: v };
    }
    return base;
  }, [assessment?.responses, pendingChanges]);

  const liveMetrics = useMemo(() => metricsFromResponses(localResponses), [localResponses]);
  const currentMeasurement = useMemo(() => (assessment ? {
    assessment_id: assessment.id,
    date: assessment.started_at,
    weight_kg: Number(localResponses.vitals_weight_kg?.value) || null,
    height_cm: Number(localResponses.vitals_height_cm?.value) || null,
    waist_cm: Number(localResponses.vitals_waist_cm?.value) || null,
    ...liveMetrics,
  } : null), [assessment, localResponses, liveMetrics]);

  async function addModule(key) {
    try {
      const r = await api.post(`/assessments/${id}/modules`, { module_key: key, action: "add" });
      setAssessment(r.data);
      await loadModuleSchema(key);
      // Re-read so the new module is pre-filled from onboarding.
      refreshAssessment().catch(() => {});
      setShowAddModule(false);
      toast.success(`${key} module added`);
    } catch (e) {
      toast.error("Could not add module", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function hideModule(key) {
    if (!window.confirm(`Hide the ${key} module? Its data will be retained and shown on the review screen.`)) return;
    try {
      const r = await api.post(`/assessments/${id}/modules`, { module_key: key, action: "hide" });
      setAssessment(r.data);
    } catch (e) {
      toast.error("Could not hide module", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function restoreModule(key) {
    try {
      const r = await api.post(`/assessments/${id}/modules`, { module_key: key, action: "restore" });
      setAssessment(r.data);
    } catch (e) {
      toast.error("Could not restore module", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function saveConsent(status) {
    try {
      const r = await api.post(`/assessments/${id}/consent`, { status });
      setAssessment(r.data);
      setShowConsent(false);
      toast.success("Consent recorded", {
        description: r.data?.recording_consent_on_file ? "Saved to the client's Health Record — you won't be asked again." : undefined,
      });
    } catch (e) {
      toast.error("Could not record consent", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function finalise() {
    // Open the finalise confirmation dialog so the practitioner can
    // pick / confirm the signature block before locking the record.
    setShowFinaliseDialog(true);
  }

  async function confirmFinalise() {
    setFinalising(true);
    await flushChanges();
    try {
      const r = await api.post(`/assessments/${id}/finalise`, {
        signature_id: signatureId || null,
      });
      setAssessment(r.data);
      setShowFinaliseDialog(false);
      toast.success("Assessment finalised");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "unacknowledged_critical_alerts") {
        toast.error("Acknowledge critical alerts before finalising", {
          description: `${detail.blocking_alerts?.length || 0} alert(s) blocking. Scroll up to the safety banner.`,
        });
        setShowFinaliseDialog(false);
      } else {
        toast.error("Finalise failed", { description: e?.response?.data?.detail || e.message });
      }
    } finally {
      setFinalising(false);
    }
  }

  async function acknowledgeAlert(ruleKey) {
    try {
      const r = await api.post(`/assessments/${id}/acknowledge`, { rule_key: ruleKey });
      setAssessment(r.data);
      toast.success("Acknowledged");
    } catch (e) {
      toast.error("Could not acknowledge", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function startReassessment() {
    if (!window.confirm("Start a reassessment based on this one? The previous values will appear inline for comparison.")) return;
    try {
      const r = await api.post(`/assessments/${id}/reassess`);
      toast.success("Reassessment created");
      nav(`/assessments/${r.data.id}`);
    } catch (e) {
      toast.error("Could not create reassessment", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function confirmReopen() {
    const reason = (reopenReason || "").trim();
    if (reason.length < 3) {
      toast.error("Please describe why you're reopening this assessment.");
      return;
    }
    setReopening(true);
    try {
      const r = await api.post(`/assessments/${id}/reopen`, { reason });
      setAssessment(r.data);
      setShowReopen(false);
      setReopenReason("");
      toast.success("Assessment reopened", {
        description: "You can now amend it. The prior sign-off is preserved in the audit trail.",
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.error || e.message);
      if (msg === "not_authorised_to_reopen") {
        toast.error("Only the Owner or the original finaliser can reopen this assessment.");
      } else if (msg === "not_finalised") {
        toast.error("This assessment isn't finalised — nothing to reopen.");
      } else {
        toast.error("Could not reopen", { description: msg });
      }
    } finally {
      setReopening(false);
    }
  }

  // ---- Search palette ------------------------------------------------------
  const searchIndex = useMemo(() => {
    if (!assessment) return [];
    const rows = [];
    for (const slot of (assessment.modules || []).filter((m) => !m.hidden)) {
      const schema = schemas[slot.key];
      if (!schema) continue;
      for (const section of schema.sections || []) {
        for (const field of section.fields || []) {
          rows.push({
            key: `${slot.key}::${section.key}::${field.key}`,
            module_key: slot.key,
            module_label: schema.label,
            section_key: section.key,
            section_label: section.label,
            field_key: field.key,
            field_label: field.label,
            field_help: field.help || "",
            field_type: field.type,
          });
        }
      }
    }
    return rows;
  }, [assessment, schemas]);

  const paletteResults = useMemo(() => {
    const needle = paletteQuery.trim().toLowerCase();
    if (!needle) return searchIndex.slice(0, 25);
    const terms = needle.split(/\s+/).filter(Boolean);
    const matches = [];
    for (const row of searchIndex) {
      const hay = `${row.field_label} ${row.section_label} ${row.module_label} ${row.field_help} ${row.field_key}`.toLowerCase();
      if (terms.every((t) => hay.includes(t))) matches.push(row);
      if (matches.length >= 40) break;
    }
    return matches;
  }, [paletteQuery, searchIndex]);

  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  async function jumpToField(row) {
    // Expand the parent section if closed
    const openKey = `${row.module_key}::${row.section_key}`;
    setOpenSections((p) => ({ ...p, [openKey]: true }));
    setPaletteOpen(false);
    setPaletteQuery("");
    // Wait one frame for the section to render, then scroll & highlight
    setTimeout(() => {
      const el = document.querySelector(`[data-field-wrapper="${row.field_key}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-zinc-950", "ring-offset-2", "rounded");
        setTimeout(() => el.classList.remove("ring-2", "ring-zinc-950", "ring-offset-2", "rounded"), 1800);
      }
    }, 120);
  }

  async function downloadPdf() {
    try {
      const r = await api.get(`/assessments/${id}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const slug = (assessment.client_name || "assessment").toLowerCase().replace(/\s+/g, "-");
      a.href = url;
      a.download = `assessment-${slug}-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function handleRecordingSaved(soapNote) {
    // When the SoapSessionDialog produces a note we associate it with this
    // assessment so the audit trail shows the linkage.
    if (!soapNote?.session_id) return;
    try {
      await api.post(`/assessments/${id}/link-soap-session`, {
        soap_session_id: soapNote.session_id,
      });
      const r = await api.get(`/assessments/${id}`);
      setAssessment(r.data);
    } catch { /* ignore — assessment still saved */ }
  }

  function toggleSection(mkey, skey) {
    const key = `${mkey}::${skey}`;
    setOpenSections((p) => ({ ...p, [key]: !p[key] }));
  }

  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  if (!assessment) return <div className="p-8 text-sm text-zinc-500">Assessment not found.</div>;

  const consentOnFile = !!assessment.recording_consent_on_file;
  const canRecord = !["declined", "withdrawn"].includes(assessment.consent?.status)
    && (consentOnFile || !!assessment.consent?.status);
  const activeModules = (assessment.modules || []).filter((m) => !m.hidden);
  const hiddenModules = (assessment.modules || []).filter((m) => m.hidden);
  const availableToAdd = modules.filter((m) =>
    !m.always_on && !(assessment.modules || []).some((x) => x.key === m.key)
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
      {/* Sticky header */}
      <div
        className="sticky top-0 z-30 bg-white/95 backdrop-blur -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 border-b border-zinc-200"
        data-testid="assessment-header"
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <Link to="/assessments" className="text-xs text-zinc-500 hover:text-zinc-900">← All assessments</Link>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              <Stethoscope className="h-5 w-5 text-zinc-500" />
              <h1 className="font-display text-xl font-bold text-zinc-950 truncate">{assessment.client_name}</h1>
              <Badge variant={assessment.status === "finalised" ? "default" : "outline"} data-testid="assessment-status">
                {assessment.status}
              </Badge>
              {amendmentCount > 0 && (
                <Badge
                  variant="outline"
                  className="border-amber-400 text-amber-800 bg-amber-50"
                  data-testid="assessment-amended-badge"
                  title={`Reopened ${amendmentCount} time(s) after original sign-off — see audit trail`}
                >
                  Amended · {amendmentCount} revision{amendmentCount > 1 ? "s" : ""}
                </Badge>
              )}
              <span className="text-xs text-zinc-500">
                started {new Date(assessment.started_at).toLocaleString()}
              </span>
              <span className="text-xs text-zinc-500">· {assessment.practitioner_name}</span>
            </div>
            <div className="mt-1">
              <LinkedIntakeControl
                assessment={assessment}
                readOnly={readOnly}
                onLinked={(d) => {
                  if (d?.assessment) setAssessment(d.assessment);
                  setAutoFilledFields((prev) => {
                    const next = new Set(prev);
                    for (const x of d?.seeded || []) next.add(x.field_key);
                    return next;
                  });
                }}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <SaveStatePill state={saveState} onRetry={flushChanges} lastSavedAt={lastSavedAt} />
            {!readOnly && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowConsent(true)}
                data-testid="assessment-consent-button"
              >
                <Mic className="h-3.5 w-3.5 mr-1.5" />
                {assessment.consent?.status
                  ? `Consent: ${assessment.consent.status}`
                  : consentOnFile ? "Consent on file" : "Record consent"}
              </Button>
            )}
            {!readOnly && canRecord && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRecorderOpen(true)}
                className="border-red-200 text-red-800 hover:bg-red-50"
                data-testid="assessment-record-button"
              >
                <Mic className="h-3.5 w-3.5 mr-1.5" />
                {assessment.linked_soap_session_id ? "Recording linked" : "Record"}
              </Button>
            )}
            {!readOnly && !assessment.linked_soap_session_id && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setLinkRecordingOpen(true)}
                className="border-zinc-300 text-zinc-700"
                data-testid="assessment-link-existing-recording"
                title="Attach a SOAP note you already recorded for this client — the transcript will populate this assessment"
              >
                <Link2 className="h-3.5 w-3.5 mr-1.5" /> Link existing recording
              </Button>
            )}
            {!readOnly && signatures.length > 0 && (
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-zinc-500 hidden sm:inline">Sig</span>
                <select
                  value={signatureId || ""}
                  onChange={(e) => saveSignatureSelection(e.target.value)}
                  className="border border-zinc-300 rounded-md h-8 px-1.5 text-xs bg-white max-w-[180px]"
                  data-testid="assessment-signature-picker"
                  title="Signature block used on the exported PDF"
                >
                  <option value="">
                    {`Default${signatures.find((s) => s.is_default) ? ` — ${signatures.find((s) => s.is_default).name}` : ""}`}
                  </option>
                  {signatures.map((s) => (
                    <option key={s.id} value={s.id}>{`${s.name}${s.is_default ? " (default)" : ""}`}</option>
                  ))}
                </select>
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPaletteOpen(true)}
              data-testid="assessment-search-button"
              className="gap-1.5"
              title="Search fields (Ctrl/⌘ + K)"
            >
              <Search className="h-3.5 w-3.5" />
              <span>Search</span>
              <kbd className="ml-1 text-2xs font-mono px-1 py-0.5 rounded bg-zinc-100 text-zinc-500 border border-zinc-200">⌘K</kbd>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={downloadPdf}
              data-testid="assessment-pdf-button"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> PDF
            </Button>
            <PrintFormButton testid="assessment-print-form-button" assessmentId={id} />
            <SendResourceButton
              client={{ email: extractEmailFromClientId(assessment.client_id), name: assessment.client_name,
                        canonicalId: assessment.canonical_client_id || null }}
              source={{ kind: "assessment", id }} testId="assessment-send-resource-btn"
            />

            {readOnly && (
              <Button
                size="sm"
                variant="outline"
                onClick={startReassessment}
                data-testid="assessment-reassess-button"
              >
                <History className="h-3.5 w-3.5 mr-1.5" /> Reassess
              </Button>
            )}
            {canReopen && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setReopenReason(""); setShowReopen(true); }}
                className="border-amber-300 text-amber-800 hover:bg-amber-50"
                data-testid="assessment-reopen-button"
                title="Reopen this finalised assessment to add or amend information. The prior sign-off is preserved."
              >
                <Unlock className="h-3.5 w-3.5 mr-1.5" /> Reopen to amend
              </Button>
            )}
            {!readOnly && (
              <Button
                size="sm"
                variant="outline"
                onClick={prefillFromOnboarding}
                disabled={manualPrefilling}
                data-testid="assessment-prefill-onboarding"
              >
                <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                {manualPrefilling ? "Prefilling…" : "Prefill from onboarding"}
              </Button>
            )}
            {!readOnly && assessment.linked_soap_session_id && !recActiveForThis && (
              <Button
                size="sm"
                variant="outline"
                onClick={generateSuggestions}
                disabled={suggestionsLoading}
                data-testid="assessment-suggest-button"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                {suggestionsLoading ? "Extracting…" : "Suggest from transcript"}
              </Button>
            )}
            {!readOnly && (
              <Button
                size="sm"
                onClick={finalise}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="assessment-finalise"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Finalise
              </Button>
            )}
            {isAdmin && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowDelete(true)}
                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
                data-testid="assessment-delete"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
              </Button>
            )}
          </div>
        </div>

        {/* Module chips */}
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {activeModules.map((m) => (
            <div
              key={m.key}
              data-testid={`assessment-active-module-${m.key}`}
              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full border border-zinc-300 bg-white text-xs"
            >
              <span className="capitalize">{m.key}</span>
              {m.key !== "core" && !readOnly && (
                <button
                  type="button"
                  onClick={() => hideModule(m.key)}
                  className="rounded-full hover:bg-zinc-200 h-5 w-5 flex items-center justify-center text-zinc-600"
                  data-testid={`assessment-hide-module-${m.key}`}
                  aria-label={`Hide ${m.key}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
          {hiddenModules.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => restoreModule(m.key)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed border-zinc-300 bg-zinc-50 text-xs text-zinc-500"
              data-testid={`assessment-hidden-module-${m.key}`}
            >
              <RotateCw className="h-3 w-3" /> {m.key} (hidden)
            </button>
          ))}
          {!readOnly && availableToAdd.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              onClick={() => setShowAddModule(true)}
              data-testid="assessment-add-module-button"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add module
            </Button>
          )}
        </div>
      </div>

      {/* Consent banner */}
      {!assessment.consent && assessment.recording_consent_on_file === false && !readOnly && (
        <Card className="p-3 border border-amber-200 bg-amber-50 text-sm flex items-start gap-2" data-testid="consent-banner">
          <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5" />
          <div>
            Record recording consent before starting the voice recorder.
            <button onClick={() => setShowConsent(true)} className="ml-2 underline underline-offset-2 font-medium text-amber-900">
              Record consent now
            </button>
          </div>
        </Card>
      )}

      {/* Reassessment banner */}
      {assessment.previous_assessment_id && (
        <Card className="p-3 border border-sky-200 bg-sky-50 text-sm flex items-start gap-2" data-testid="reassess-banner">
          <History className="h-4 w-4 text-sky-700 mt-0.5" />
          <div>
            <b>Reassessment</b> — baseline values from the previous assessment appear beneath each
            field so you can spot change without leaving the page.
            {" "}
            <Link
              to={`/assessments/${assessment.previous_assessment_id}`}
              className="underline underline-offset-2 font-medium text-sky-900"
            >
              View the original
            </Link>.
          </div>
        </Card>
      )}

      {/* Decision-support alerts */}
      {(assessment.alerts || []).length > 0 && (
        <AlertsBanner
          alerts={assessment.alerts}
          onAcknowledge={acknowledgeAlert}
          readOnly={readOnly}
        />
      )}

      {(assessment.onboarding_changes || []).length > 0 && (
        <OnboardingChangesBanner
          changes={assessment.onboarding_changes}
          finalised={readOnly}
          onReview={() => setShowOnboardingChanges(true)}
        />
      )}
      <OnboardingChangesDialog
        open={showOnboardingChanges}
        onOpenChange={setShowOnboardingChanges}
        assessmentId={assessment.id}
        changes={assessment.onboarding_changes || []}
        finalised={readOnly}
        onResolved={(doc) => setAssessment(doc)}
      />

      {/* Transcript suggestions — spec-compliant review queue */}
      <TranscriptReviewPane
        suggestions={suggestions}
        filteredSuggestions={filteredSuggestions}
        speakerFilter={speakerFilter}
        experiencerFilter={experiencerFilter}
        setSpeakerFilter={setSpeakerFilter}
        setExperiencerFilter={setExperiencerFilter}
        acceptSuggestion={acceptSuggestion}
        rejectSuggestion={rejectSuggestion}
        deferSuggestion={deferSuggestion}
        clarifySuggestion={clarifySuggestion}
        batchAcceptSafe={batchAcceptSafe}
        setEvidenceSuggestion={setEvidenceSuggestion}
        setReportSuggestion={setReportSuggestion}
        readOnly={readOnly}
      />

      {/* Sections */}
      {activeModules.map((m) => {
        const schema = schemas[m.key];
        if (!schema) return <Card key={m.key} className="p-4 text-sm text-zinc-500">Loading {m.key}…</Card>;
        // v2.0 safety-critical alerts for this module. Only fires
        // when the field is currently visible under showIf and the
        // clinician has recorded a truthy answer (spec §4.3).
        const flatResponses = flattenResponses(localResponses);
        const moduleFields = [];
        for (const s of (schema.sections || [])) {
          for (const f of (s.fields || [])) moduleFields.push({ ...f, id: f.key });
        }
        const safetyAlerts = v2GetSafetyAlerts(moduleFields, flatResponses);
        return (
          <Card key={m.key} className="p-4 border border-zinc-200" data-testid={`assessment-module-${m.key}`}>
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList className="h-4 w-4 text-zinc-500" />
              <h2 className="font-display text-lg font-bold text-zinc-950">{schema.label}</h2>
              <Badge variant="outline" className="text-2xs">v{schema.version}</Badge>
            </div>
            {safetyAlerts.length > 0 && (
              <div
                className="rounded-md border border-rose-300 bg-rose-50 p-3 mb-3 text-sm"
                role="alert"
                data-testid={`assessment-safety-banner-${m.key}`}
              >
                <div className="font-semibold text-rose-900 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4" />
                  Safety-critical answers require review
                </div>
                <ul className="mt-1 pl-6 list-disc space-y-0.5 text-xs text-rose-950">
                  {safetyAlerts.map((a) => (
                    <li key={a.fieldId}
                        data-testid={`assessment-safety-item-${a.fieldId}`}>
                      <span className="font-medium">{a.label}:</span>{" "}
                      {V2_ALERT_MESSAGES[a.rule] || "Requires clinician review."}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="space-y-2">
              {(schema.sections || []).map((section) => {
                const openKey = `${m.key}::${section.key}`;
                const open = openSections[openKey];
                const pendingAI = pendingBySectionKey.get(section.key) || 0;
                return (
                  <div key={section.key} className="rounded border border-zinc-200">
                    <button
                      type="button"
                      onClick={() => toggleSection(m.key, section.key)}
                      className="w-full text-left px-3 py-2 flex items-center justify-between bg-zinc-50 hover:bg-zinc-100"
                      data-testid={`assessment-section-toggle-${m.key}-${section.key}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-zinc-900 text-sm">{section.label}</div>
                        {pendingAI > 0 && (
                          <span
                            className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-violet-300 bg-violet-100 text-violet-900 inline-flex items-center gap-0.5"
                            title={`${pendingAI} pending AI transcript suggestion${pendingAI === 1 ? "" : "s"} in this section`}
                            data-testid={`section-ai-badge-${section.key}`}
                          >
                            <Mic className="h-2.5 w-2.5" />
                            {pendingAI} pending
                          </span>
                        )}
                      </div>
                      {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {open && (
                      <div className="p-3 space-y-3">
                        {(section.fields || []).filter((f) => evaluateShowIf(f, localResponses)).map((field) => {
                          const resp = field.computed
                            ? { value: liveMetrics[field.computed], source: "computed" }
                            : localResponses[field.key];
                          const isBoundToCore = !!field.bindsToCanonical && !!field.coreFieldKey;
                          return (
                            <div key={field.key} className="grid gap-1.5" data-field-wrapper={field.key}>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Label className="text-sm">
                                  {field.label}
                                  {field.required && <span className="text-red-700 ml-0.5">*</span>}
                                </Label>
                                {!field.computed && <ResponseChip resp={resp} />}
                                {isBoundToCore && (
                                  <>
                                    <span
                                      className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-violet-200 bg-violet-50 text-violet-900 inline-flex items-center gap-0.5"
                                      title={`Managed in Core — this value mirrors the "${field.coreFieldKey}" field. Edit it there and it will update everywhere.`}
                                      data-testid={`bound-to-core-chip-${field.key}`}
                                    >
                                      <Link2 className="h-2.5 w-2.5" />
                                      managed in Core
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => jumpToCoreField(field.coreFieldKey)}
                                      className="text-2xs font-medium text-indigo-700 hover:text-indigo-900 underline underline-offset-2"
                                      data-testid={`edit-in-core-${field.key}`}
                                    >
                                      Edit in Core →
                                    </button>
                                  </>
                                )}
                                {(autoFilledFields.has(field.key) || resp?.prefilled_from) && (
                                  <span
                                    className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-900 inline-flex items-center gap-0.5"
                                    title={`Auto-filled from onboarding${resp?.prefilled_from ? ` (${resp.prefilled_from})` : ""}. Overtype to override.`}
                                    data-testid={`auto-filled-chip-${field.key}`}
                                  >
                                    <Sparkles className="h-2.5 w-2.5" />
                                    auto-filled from onboarding
                                  </span>
                                )}
                                {changesByField.get(field.key) && (
                                  <OnboardingChangeChip
                                    change={changesByField.get(field.key)}
                                    onClick={() => setShowOnboardingChanges(true)}
                                  />
                                )}
                                {suggestionsByField.get(field.key) && !readOnly && !isBoundToCore && (
                                  <TranscriptSuggestionPill
                                    suggestion={suggestionsByField.get(field.key)}
                                    onAccept={acceptSuggestion}
                                    onOpenComparison={(sid) => setComparisonSuggestionId(sid)}
                                    onReject={rejectSuggestion}
                                    onReport={(s) => setReportSuggestion(s)}
                                    onOpenEvidence={(s) => setEvidenceSuggestion(s)}
                                  />
                                )}
                                {resp?.original_value !== undefined && resp?.original_value !== null && (
                                  <span className="text-2xs text-zinc-500">
                                    original: {String(resp.original_value)}
                                  </span>
                                )}
                                {previousResponses[field.key] && (
                                  <span
                                    className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-sky-200 bg-sky-50 text-sky-900"
                                    data-testid={`previous-value-${field.key}`}
                                    title={previousResponses[field.key].updated_at || ""}
                                  >
                                    previous: {String(previousResponses[field.key].value)}
                                  </span>
                                )}
                              </div>
                              {field.help && <div className="text-xs text-zinc-500">{field.help}</div>}
                              <FieldRenderer field={field} resp={resp} onChange={setFieldValue} readOnly={readOnly || isBoundToCore} />
                              {field.key === "ep_grip_strength_kg" && (
                                <GripNormsHint assessmentId={assessment.id} text={resp?.value} />
                              )}
                            </div>
                          );
                        })}
                        {m.key === "core" && section.key === "vitals" && (
                          <MeasurementTrend
                            clientId={assessment.client_id}
                            canonicalClientId={assessment.canonical_client_id}
                            current={currentMeasurement}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Phase 3b — MSK region instances live inside their host
                pathway module (Clinical Pilates / EP) or the standalone
                MSK module. Renders zero-to-many regions with drag-reorder
                + legacy migration banner. */}
            {["clinical_pilates", "exercise_physiology", "msk"].includes(m.key) && (
              <div className="mt-4" data-testid={`msk-regions-slot-${m.key}`}>
                <MskRegionsPanel assessmentId={id} readOnly={readOnly} />
              </div>
            )}
          </Card>
        );
      })}

      <SbnResultsSection assessment={assessment} readOnly={readOnly} onUpdated={setAssessment} />

      {/* Add module dialog */}
      <Dialog open={showAddModule} onOpenChange={setShowAddModule}>
        <DialogContent data-testid="add-module-dialog">
          <DialogHeader>
            <DialogTitle>Add a module</DialogTitle>
            <DialogDescription>
              Add a module on the fly if new findings emerge during the consultation. Existing data
              is never overwritten.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            {availableToAdd.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => addModule(m.key)}
                className="text-left p-3 rounded border border-zinc-200 hover:border-zinc-950 hover:bg-zinc-50"
                data-testid={`add-module-${m.key}`}
              >
                <div className="font-semibold text-zinc-950">{m.label}</div>
                <div className="text-xs text-zinc-600 mt-0.5">{m.description}</div>
              </button>
            ))}
            {availableToAdd.length === 0 && (
              <div className="text-sm text-zinc-500">No further modules available.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Consent dialog */}
      <Dialog open={showConsent} onOpenChange={setShowConsent}>
        <DialogContent data-testid="consent-dialog">
          <DialogHeader>
            <DialogTitle>Recording consent</DialogTitle>
            <DialogDescription>
              Confirm the client&apos;s consent status before starting the voice recorder.
              Verbal or written consent is saved to their Health Record, so you&apos;re only asked once per client.
              Recording must never start automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            {CONSENT_OPTIONS.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => saveConsent(o.v)}
                className={`text-left p-3 rounded border ${
                  assessment.consent?.status === o.v
                    ? "border-zinc-950 bg-zinc-50"
                    : "border-zinc-200 hover:border-zinc-950"
                }`}
                data-testid={`consent-option-${o.v}`}
              >
                <div className="font-semibold text-zinc-950">{o.label}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Finalise confirmation with signature picker (mirrors the Letters flow) */}
      <DeleteWithReasonDialog
        open={showDelete}
        onOpenChange={setShowDelete}
        title="Delete this assessment?"
        description="The assessment will be hidden from the client's timeline. An admin can restore it from Deleted Items."
        itemLabel={
          assessment
            ? `${assessment.client_name || "Assessment"} · ${(assessment.started_at || "").slice(0, 10)} · ${assessment.status}`
            : ""
        }
        confirmLabel="Delete assessment"
        onConfirm={async (reason) => {
          await api.delete(`/assessments/${id}`, { data: { reason } });
          toast.success("Assessment deleted");
          nav(-1);
        }}
        testidPrefix="assessment-delete"
      />
      <Dialog open={showFinaliseDialog} onOpenChange={(v) => !finalising && setShowFinaliseDialog(v)}>
        <DialogContent data-testid="finalise-dialog">
          <DialogHeader>
            <DialogTitle>Finalise assessment</DialogTitle>
            <DialogDescription>
              Locking this record prevents further edits. Amendments can be recorded but the original stays preserved.
              Choose the signature block that should appear on the exported PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Signature</Label>
              {signatures.length === 0 ? (
                <div className="mt-1 text-sm text-zinc-500 italic">
                  No signature blocks saved yet — the code defaults will be used.
                  Add one under <Link to="/settings" className="underline">Settings → Signature blocks</Link>.
                </div>
              ) : (
                <select
                  value={signatureId || ""}
                  onChange={(e) => setSignatureId(e.target.value)}
                  className="mt-1 border border-zinc-300 rounded-md h-9 px-2 text-sm bg-white w-full"
                  data-testid="finalise-signature-picker"
                >
                  <option value="">
                    {`Use default${signatures.find((s) => s.is_default) ? ` — ${signatures.find((s) => s.is_default).name}` : ""}`}
                  </option>
                  {signatures.map((s) => (
                    <option key={s.id} value={s.id}>
                      {`${s.name}${s.title ? ` · ${s.title}` : ""}${s.is_default ? "  (default)" : ""}`}
                    </option>
                  ))}
                </select>
              )}
              <p className="mt-1 text-2xs text-zinc-500">
                The same picker sits in the header — pick anytime before finalising.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowFinaliseDialog(false)}
              disabled={finalising}
              data-testid="finalise-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmFinalise}
              disabled={finalising}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="finalise-confirm"
            >
              {finalising ? "Finalising…" : "Finalise & lock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen (amend) dialog — audit-heavy: reason REQUIRED */}
      <Dialog open={showReopen} onOpenChange={(v) => !reopening && setShowReopen(v)}>
        <DialogContent data-testid="reopen-dialog">
          <DialogHeader>
            <DialogTitle>Reopen this assessment to amend it</DialogTitle>
            <DialogDescription>
              The original sign-off ({assessment?.finalised_by_name || "practitioner"} · {(assessment?.finalised_at || "").slice(0, 10)})
              is preserved in the amendment history and audit trail. When you finalise again, this record will be marked as amended.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label htmlFor="reopen-reason" className="text-xs uppercase tracking-wider text-zinc-500">
                Reason for reopening <span className="text-red-600">*</span>
              </Label>
              <Textarea
                id="reopen-reason"
                rows={3}
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="e.g. Client called back with new investigation results — adding to Objective and updating plan."
                className="mt-1"
                data-testid="reopen-reason-input"
                disabled={reopening}
              />
              <p className="mt-1 text-2xs text-zinc-500">
                This reason is stored on the audit trail (visible under History) — please be specific.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowReopen(false)}
              disabled={reopening}
              data-testid="reopen-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmReopen}
              disabled={reopening || (reopenReason || "").trim().length < 3}
              className="bg-amber-700 hover:bg-amber-800 text-white"
              data-testid="reopen-confirm"
            >
              {reopening ? "Reopening…" : "Reopen for amendment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voice recorder (dialog-based; keeps recording via top-level RecordingProvider even when closed) */}
      {recorderOpen && (
        <SoapSessionDialog
          open={recorderOpen}
          onClose={() => setRecorderOpen(false)}
          clientEmail={extractEmailFromClientId(assessment.client_id)}
          clientName={assessment.client_name}
          onSaved={handleRecordingSaved}
          initiatingAssessmentId={id}
        />
      )}

      {/* Manual "Link existing recording" picker — for pre-fix
          orphan SOAP notes that were recorded from the client
          Overview and never got auto-linked. */}
      {linkRecordingOpen && (
        <LinkExistingRecordingDialog
          open={linkRecordingOpen}
          onClose={() => setLinkRecordingOpen(false)}
          clientEmail={extractEmailFromClientId(assessment.client_id)}
          assessmentId={id}
          onLinked={async () => {
            setLinkRecordingOpen(false);
            // Reload the assessment (picks up the new
            // ``linked_soap_session_id``) and the suggestions
            // (which the auto-extract in the link dialog just
            // populated, best-effort).
            await Promise.all([refreshAssessment(), refreshSuggestions()]);
            toast.success("Recording linked", {
              description: "Suggestions from the transcript have been extracted — review them in the Suggest from transcript panel.",
            });
          }}
        />
      )}

      {/* Transcript-suggestion comparison view (spec §5-6) */}
      <SuggestionComparisonDialog
        open={!!comparisonSuggestionId}
        suggestionId={comparisonSuggestionId}
        onClose={async (applied) => {
          setComparisonSuggestionId(null);
          if (applied) {
            await Promise.all([refreshAssessment(), refreshSuggestions()]);
            toast.success("Suggestion applied after comparison");
          } else {
            // Even a "keep existing" writes a rejected state — refresh
            // so the row leaves the queue.
            await refreshSuggestions();
          }
        }}
      />

      {/* Transcript-error feedback dialog (spec §11 — corpus for prompt tuning) */}
      <ReportTranscriptionErrorDialog
        open={!!reportSuggestion}
        suggestion={reportSuggestion}
        onClose={async (reported) => {
          setReportSuggestion(null);
          if (reported) {
            await refreshSuggestions();
          }
        }}
      />

      {/* Evidence drawer (spec TAF-011 — audio replay from timestamp) */}
      <EvidenceDrawer
        open={!!evidenceSuggestion}
        suggestion={evidenceSuggestion}
        onClose={() => setEvidenceSuggestion(null)}
      />

      {/* Search palette (⌘K / Ctrl+K) */}
      <Dialog open={paletteOpen} onOpenChange={(v) => { setPaletteOpen(v); if (!v) setPaletteQuery(""); }}>
        <DialogContent className="max-w-2xl p-0 gap-0" data-testid="search-palette">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-200">
            <Search className="h-4 w-4 text-zinc-400" />
            <Input
              autoFocus
              value={paletteQuery}
              onChange={(e) => setPaletteQuery(e.target.value)}
              placeholder="Search fields, e.g. 'chair stand', 'seizure', 'bone mets', 'gait speed'"
              className="border-0 shadow-none focus-visible:ring-0 h-9 px-0 text-sm"
              data-testid="search-palette-input"
            />
            <kbd className="text-2xs font-mono px-1 py-0.5 rounded bg-zinc-100 text-zinc-500 border border-zinc-200">esc</kbd>
          </div>
          <div className="max-h-[60vh] overflow-y-auto" data-testid="search-palette-results">
            {paletteResults.length === 0 && (
              <div className="p-6 text-center text-sm text-zinc-500">
                No fields match &quot;{paletteQuery}&quot;.
              </div>
            )}
            {paletteResults.map((row) => (
              <button
                key={row.key}
                type="button"
                onClick={() => jumpToField(row)}
                className="w-full text-left px-4 py-2.5 hover:bg-zinc-50 border-b border-zinc-100 flex items-start gap-3"
                data-testid={`search-result-${row.field_key}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-950 truncate">{row.field_label}</div>
                  <div className="text-xs text-zinc-500 truncate">
                    {row.module_label} · {row.section_label}
                  </div>
                </div>
                <span className="text-2xs font-mono uppercase tracking-wider text-zinc-400 shrink-0 mt-0.5">
                  {row.field_type}
                </span>
              </button>
            ))}
          </div>
          <div className="px-4 py-2 text-2xs text-zinc-500 border-t border-zinc-200 flex items-center gap-3 flex-wrap">
            <span>{paletteResults.length} of {searchIndex.length} fields</span>
            <span className="ml-auto">Tip: press <kbd className="font-mono px-1 py-0.5 rounded bg-zinc-100 text-zinc-500 border border-zinc-200">⌘K</kbd> anywhere in the editor</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Client IDs are stored as ``"acuity:<email>"`` — this pulls the email
 *  back out for the recorder. Non-acuity clients (e.g. manual) fall back
 *  to the raw client_id so the recorder still works. */
function extractEmailFromClientId(clientId) {
  if (!clientId) return "";
  const idx = clientId.indexOf(":");
  return idx >= 0 ? clientId.slice(idx + 1) : clientId;
}

function AlertsBanner({ alerts, onAcknowledge, readOnly }) {
  const severityCfg = {
    critical: { cls: "border-red-300 bg-red-50 text-red-950", pill: "bg-red-600 text-white" },
    warning:  { cls: "border-amber-300 bg-amber-50 text-amber-950", pill: "bg-amber-600 text-white" },
    info:     { cls: "border-sky-300 bg-sky-50 text-sky-950", pill: "bg-sky-600 text-white" },
  };
  const grouped = { critical: [], warning: [], info: [] };
  for (const a of alerts) grouped[a.severity]?.push(a);

  return (
    <div className="space-y-2" data-testid="alerts-banner">
      {["critical", "warning", "info"].map((sev) => {
        const rows = grouped[sev];
        if (!rows.length) return null;
        const cfg = severityCfg[sev];
        return (
          <Card key={sev} className={`p-3 border ${cfg.cls}`} data-testid={`alerts-${sev}`}>
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className="h-4 w-4" />
              <span className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded ${cfg.pill}`}>
                {sev}
              </span>
              <span className="text-xs font-medium">
                {rows.length} {rows.length === 1 ? "alert" : "alerts"}
              </span>
            </div>
            <div className="space-y-2">
              {rows.map((a) => (
                <div
                  key={a.key}
                  className="bg-white/80 border border-zinc-200 rounded p-2"
                  data-testid={`alert-${a.key}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-zinc-950 text-sm flex items-center gap-2">
                        {a.title}
                        {a.acknowledged && (
                          <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-800">
                            acknowledged
                          </span>
                        )}
                        {a.stale && (
                          <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-800">
                            value changed — re-ack required
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-700 mt-1">{a.message}</div>
                      <div className="text-2xs uppercase tracking-wider text-zinc-500 mt-1">
                        triggered by <span className="font-mono">{a.field}</span> = {String(a.triggered_value)}
                      </div>
                    </div>
                    {!a.acknowledged && !readOnly && (
                      <Button
                        size="sm"
                        onClick={() => onAcknowledge(a.key)}
                        className={sev === "critical" ? "bg-red-700 hover:bg-red-800 text-white" : ""}
                        data-testid={`alert-ack-${a.key}`}
                      >
                        Acknowledge
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function SaveStatePill({ state, onRetry, lastSavedAt }) {
  // Re-render every 15s so a "Saved 30s ago" label stays fresh
  // without the parent needing to broadcast ticks.
  const [, tick] = useState(0);
  useEffect(() => {
    if (state !== "saved" || !lastSavedAt) return;
    const id = setInterval(() => tick((n) => n + 1), 15000);
    return () => clearInterval(id);
  }, [state, lastSavedAt]);

  const relativeSavedLabel = (() => {
    if (state !== "saved") return "Saved";
    if (!lastSavedAt) return "Saved";
    const diff = Math.max(0, Date.now() - lastSavedAt);
    if (diff < 5_000) return "Saved just now";
    if (diff < 60_000) return `Saved ${Math.round(diff / 1000)}s ago`;
    if (diff < 3_600_000) return `Saved ${Math.round(diff / 60_000)}m ago`;
    return `Saved ${Math.round(diff / 3_600_000)}h ago`;
  })();

  const cfg = {
    saved:  { label: relativeSavedLabel, cls: "border-emerald-200 bg-emerald-50 text-emerald-800", icon: CheckCircle2 },
    saving: { label: "Autosaving…", cls: "border-sky-200 bg-sky-50 text-sky-800", icon: Save },
    dirty:  { label: "Unsaved changes", cls: "border-amber-200 bg-amber-50 text-amber-800", icon: Save },
    error:  { label: "Save failed — click to retry", cls: "border-red-200 bg-red-50 text-red-800", icon: AlertCircle },
  }[state] || { label: state, cls: "border-zinc-200 bg-zinc-50 text-zinc-700", icon: Save };
  const Icon = cfg.icon;
  return (
    <div
      onClick={state === "error" ? onRetry : undefined}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs ${cfg.cls} ${state === "error" ? "cursor-pointer" : ""}`}
      title={
        state === "saved" && lastSavedAt
          ? `Last saved ${new Date(lastSavedAt).toLocaleTimeString()}`
          : undefined
      }
      data-testid={`assessment-save-state-${state}`}
    >
      <Icon className={`h-3.5 w-3.5 ${state === "saving" ? "animate-pulse" : ""}`} />
      {cfg.label}
    </div>
  );
}



/**
 * LinkExistingRecordingDialog — picker for orphan SOAP notes.
 *
 * Rendered from the AssessmentEditor top bar when the current
 * assessment has no linked recording yet. Shows this client's
 * finalised SOAP notes that aren't attached to any other
 * assessment, and lets the operator attach one so the transcript
 * flows into the assessment (unlocks the "Suggest from
 * transcript" button).
 *
 * Fixes the "Pamela Suffolk" regression: recordings started from
 * the client Overview *before* we shipped the ``initiating_assessment_id``
 * wiring never auto-linked, and the Recovery Vault only lists
 * unfinished sessions — so pre-fix orphans had no reachable
 * place to be attached.
 */
function LinkExistingRecordingDialog({ open, onClose, clientEmail, assessmentId, onLinked }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    if (!open || !clientEmail) return;
    setLoading(true);
    // List this client's finalised SOAP notes. Each note carries a
    // ``session_id`` that we pass to the link endpoint.
    api.get(`/soap-notes/clients/${encodeURIComponent(clientEmail)}/notes`)
      .then((r) => {
        const rows = Array.isArray(r.data) ? r.data : (r.data?.notes || []);
        // Keep only notes with a resolvable session_id (older notes
        // may not have the field).
        setSessions(rows.filter((n) => n.session_id));
      })
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, [open, clientEmail]);

  async function save() {
    if (!selectedId) return;
    setSaving(true);
    try {
      // ``selectedId`` is the SOAP *session* id (not the note id).
      await api.post(`/soap-notes/sessions/${selectedId}/link-assessment`, {
        assessment_id: assessmentId,
      });
      // Auto-extract — fire the transcript suggestion pipeline
      // immediately so the operator doesn't have to click a second
      // button. Best-effort: if the SOAP session doesn't have a
      // transcript yet (edge case where a session was linked before
      // its finalise completed), the endpoint returns 400 and we
      // just swallow it — the "Suggest from transcript" button on
      // the assessment page still works as a manual fallback.
      try {
        await api.post(`/assessments/${assessmentId}/suggest-from-transcript`);
      } catch (extractErr) {
        // Only report if it's something other than the expected
        // "no transcript yet" 400 — everything else deserves a
        // heads-up so the operator knows to click Suggest manually.
        const status = extractErr?.response?.status;
        const msg = extractErr?.response?.data?.detail || "";
        if (status && status !== 400 && !/transcript/i.test(msg)) {
          toast.warning("Recording linked, but auto-suggest failed", {
            description: msg || "Click Suggest from transcript on the assessment to try again.",
          });
        }
      }
      onLinked?.();
    } catch (e) {
      toast.error("Link failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="link-recording-dialog">
        <DialogHeader>
          <DialogTitle>Link existing recording</DialogTitle>
          <DialogDescription>
            Pick a SOAP note you already recorded for this client. Its transcript
            will populate this assessment via the "Suggest from transcript" button.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-6 text-center text-sm text-zinc-500">
            <Loader2 className="h-4 w-4 mx-auto mb-2 animate-spin" /> Loading recordings…
          </div>
        ) : sessions.length === 0 ? (
          <div className="py-6 text-center text-sm text-zinc-500" data-testid="link-recording-empty">
            No recordings found for this client. Use the <b>Record</b> button on this
            page to make one — it will link automatically.
          </div>
        ) : (
          <ul className="max-h-72 overflow-y-auto divide-y divide-zinc-100 border border-zinc-200 rounded-md">
            {sessions.map((s) => (
              <li key={s.session_id}>
                <label className="flex items-center gap-2 p-2 cursor-pointer hover:bg-zinc-50">
                  <input
                    type="radio"
                    name="pick-recording"
                    checked={selectedId === s.session_id}
                    onChange={() => setSelectedId(s.session_id)}
                    data-testid={`link-recording-radio-${s.session_id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-zinc-950 truncate">
                      {s.appointment_type || "SOAP note"} · {new Date(s.created_at || s.finalised_at).toLocaleString()}
                    </div>
                    <div className="text-2xs text-zinc-500 truncate">
                      {(s.subjective || s.assessment || "").slice(0, 100) || "—"}
                    </div>
                  </div>
                </label>
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={save}
            disabled={!selectedId || saving}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="link-recording-save"
          >
            {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Linking…</> : "Link"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
